import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "./auth/useAuth";

// TODO: Replace with actual config path if needed
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export function useNotifications() {
  const [deviceId] = useLocalStorage<string>(
    "push-device-id",
    crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
  );

  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if Notification API is available
  const canEnable =
    typeof window !== "undefined" && "Notification" in window && user;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data.type === "new-message") {
        queryClient.invalidateQueries({
          queryKey: ["chat-history", user?.id, event.data.payload.character_id],
        });
        queryClient.invalidateQueries({
          queryKey: ["conversations"],
        });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [queryClient, user?.id]);

  const { data: pushSubscription, isLoading } = useQuery({
    queryKey: ["pushSubscription", deviceId],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("push_subscriptions")
        .select("*")
        .eq("device_id", deviceId)
        .eq("profile_id", user?.id)
        .maybeSingle();
      if (error) {
        throw error;
      }
      return data;
    },
    enabled: !!user?.id,
  });

  const { mutate: createPushSubscription, isPending: isSubscribing } =
    useMutation({
      mutationFn: async () => {
        if (!user?.id) {
          throw new Error("User not found");
        }
        try {
          await requestNotificationPermission();
        } catch (error) {
          const { error: insertError } = await supabase
            .from("push_subscriptions")
            .upsert({
              id: pushSubscription?.id,
              device_id: deviceId,
              device_key: null,
              profile_id: user?.id,
              enabled: false,
            });
          if (insertError) {
            throw insertError;
          }
          throw error;
        }
        const subscription = await subscribeToPushNotifications();
        if (!subscription) {
          throw new Error("No subscription");
        }
        const { data, error } = await supabase
          .from("push_subscriptions")
          .upsert({
            id: pushSubscription?.id,
            device_id: deviceId,
            device_key: JSON.stringify(subscription.toJSON()),
            profile_id: user?.id,
            enabled: true,
          });
        if (error) {
          throw error;
        }
        return data;
      },
      onError: (error) => {
        console.error("Error creating push subscription", error);
        toast.error(`Une erreur est survenue.`, { description: error.message });
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["pushSubscription", deviceId],
        });
      },
    });

  const { mutate: disablePushSubscription, isPending: isUnsubscribing } =
    useMutation({
      mutationFn: async () => {
        if (!user?.id) {
          throw new Error("User not found");
        }
        try {
          await unsubscribeFromPushNotifications();
        } catch (error) {
          console.error("Error disabling push subscription", error);
        }
        const { data, error } = await supabase
          .from("push_subscriptions")
          .upsert({
            id: pushSubscription?.id,
            device_id: deviceId,
            device_key: null,
            profile_id: user?.id,
            enabled: false,
          })
          .eq("device_id", deviceId)
          .eq("profile_id", user?.id);
        if (error) {
          throw error;
        }
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["pushSubscription", deviceId],
        });
      },
      onError: (error) => {
        console.error("Error disabling push subscription", error);
        toast.error("Une erreur est survenue.");
      },
    });

  const requestNotificationPermission = async () => {
    if (!canEnable) {
      throw new Error("Notifications non supportées sur cet appareil");
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Permission refusée");
    }
    return permission;
  };

  const subscribeToPushNotifications = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: VAPID_PUBLIC_KEY,
    });
    return subscription;
  };

  const unsubscribeFromPushNotifications = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  };

  const toggle = () => {
    if (pushSubscription?.enabled) {
      disablePushSubscription();
    } else {
      createPushSubscription();
    }
  };

  return {
    isEnabled: pushSubscription?.enabled ?? false,
    isDeviceOnboarded: isLoading ? undefined : pushSubscription !== null,
    isLoading: isLoading,
    isUpdating: isSubscribing || isUnsubscribing,
    enable: createPushSubscription,
    disable: disablePushSubscription,
    toggle,
    deviceId,
    canEnable: canEnable && !isLoading,
  };
}
